<template>
  <Card
    class="mouseover-teaser"
    :class="{
      [backgroundAnimationClass]: mouseOver,
    }"
    v-bind="cardProps"
    interactive-bg
    size="small"
    @mouseenter="mouseOver = true"
    @mouseleave="mouseOver = false"
  >
    <template #label>
      <CdnImage
        v-if="brand?.logo"
        :src="brand.logo"
        :alt="`${brand.name} Logo`"
        class="mouseover-teaser__brand"
        :image-style="`aspect-ratio: ${brand.aspectRatio}`"
        lazy
        render-without-figure
      />
    </template>
    <template #image>
      <div class="mouseover-teaser__stage">
        <slot :mouse-over="mouseOver" />
        <div class="mouseover-teaser__image">
          <slot
            name="image"
            :mouse-over="mouseOver"
          />
        </div>
        <div
          class="mouseover-teaser__content"
          :class="{
            'mouseover-teaser__content__hover': mouseOver,
          }"
        >
          <slot
            name="mouseOver"
            :mouse-over="mouseOver"
          />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { Card, type CardComponentProps } from '@i22team/td-longtail-vue-components';
import type { BrandWithLogo } from '~/lib/brand';

export type MouseOverTeaserProps = {
  /**
   * Props passed directly to the Card. Takes precedence over the shortcut props below
   * ("link", "backgroundImage", "backgroundStyle").
   */
  card?: CardComponentProps;
  backgroundAnimationClass?: string;
  brand?: BrandWithLogo;
};

const props = withDefaults(defineProps<MouseOverTeaserProps>(), {
  backgroundAnimationClass: 'mouseover-teaser--rounded',
  brand: undefined,
  card: undefined,
});

const cardProps = computed<CardComponentProps>(() => ({
  tag: 'nuxt-link',
  imagePosition: 'bottom',
  imagePositionXs: 'bottom',
  ...props.card,
}));

const mouseOver = ref(false);
</script>

<style scoped lang="scss">
.mouseover-teaser {
  --card-radius: 0;
  --card-bg-color: transparent;
  --hover-grow-width: 0px;
  --card-image-flex: 1 1 0;
  --card-image-container-height: auto;
  text-decoration: none;

  &:hover {
    --card-radius: 1.5rem;

    .mouseover-teaser__image {
      transform: scale(1.1);
    }
  }

  :deep(.card__background) {
    transition: border-radius 0.3s ease-in-out;
    background: var(--mouse-background);
  }

  // background image of the card
  :deep(.card__background img) {
    transition: transform 0.3s ease-in-out;
  }

  &__brand {
    height: 25px;
  }

  &__header {
    position: relative;
  }

  // image and content overlay each other in the same area
  &__stage {
    display: grid;
    grid-template: 100% / 100%;
    height: 100%;
  }

  &__image,
  &__content {
    grid-area: 1 / 1;
  }

  &__image {
    transition: transform 0.3s ease-in-out;
    width: 80%;
    height: 80%;
    margin: auto;
    text-align: center;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;

    :deep(picture) {
      height: 100%;
    }

    :deep(img) {
      max-height: 100%;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__content {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
}
</style>
